import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatients,
  fetchPatientById,
} from "../../store/slices/patientSlice";
import { calculateAge } from "../../utils/helpers";
import { Loader2, RefreshCw, Search, UserRound, X } from "lucide-react";

const PatientSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, loading, error } = useSelector(
    (state) => state.patients
  );

  const patientList = useMemo(
    () => (Array.isArray(patients) ? patients : patients?.data || []),
    [patients]
  );
  const [searchType, setSearchType] = useState("name");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [localSearchResults, setLocalSearchResults] = useState([]);

  // Load all patients on component mount
  useEffect(() => {
    dispatch(fetchPatients());
  }, [dispatch]);

  // After cloud/local sync (from Sync Center or elsewhere), reload the list
  useEffect(() => {
    const onSync = () => dispatch(fetchPatients());
    window.addEventListener("lis:sync-completed", onSync);
    return () => window.removeEventListener("lis:sync-completed", onSync);
  }, [dispatch]);

  // Filter patients locally based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setLocalSearchResults([]);
      return;
    }

    const filtered = patientList.filter((patient) => {
      const searchLower = searchTerm.toLowerCase();

      switch (searchType) {
        case "card":
          return patient.card_number?.toLowerCase().includes(searchLower);
        case "phone":
          return patient.phone?.includes(searchTerm);
        case "name":
        default:
          return patient.full_name?.toLowerCase().includes(searchLower);
      }
    });

    setLocalSearchResults(filtered);
  }, [searchTerm, searchType, patientList]);

  const handleSelectPatient = async (patient) => {
    try {
      // Fetch full patient details
      const result = await dispatch(fetchPatientById(patient.id)).unwrap();
      setSelectedPatient(result);
    } catch (error) {
      console.error("Failed to fetch patient details:", error);
      // Fallback to basic patient data if detailed fetch fails
      setSelectedPatient(patient);
    }
  };

  const handleAssignTests = (patient) => {
    navigate("/receptionist/test-orders", {
      state: {
        patient: patient,
        from: "patient-search",
      },
    });
  };

  const handleViewHistory = (patient) => {
    navigate(`/receptionist/patient-details/${patient.id}`, {
      state: { patient },
    });
  };

  const handleEditPatient = (patient) => {
    navigate("/receptionist/register-patient", {
      state: {
        patient: patient,
        editMode: true,
      },
    });
  };

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Reception</div>
                <h1 className="mt-1 text-xl font-semibold text-gray-900">Patient search</h1>
                <p className="mt-1 text-sm text-gray-600">Find patients by name, card number, or phone.</p>
              </div>

              <button
                type="button"
                onClick={() => dispatch(fetchPatients())}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          <div className="space-y-6 px-5 py-5 sm:px-6">
            {error ? (
              <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
                {String(error)}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Search</div>
                <div className="mt-1 text-xs text-gray-600">Start typing to see results.</div>
                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Enter ${
                      searchType === "card"
                        ? "card number"
                        : searchType === "phone"
                        ? "phone number"
                        : "patient name"
                    }…`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-2">
                {["name", "card", "phone"].map((type) => {
                  const active = searchType === type;
                  const label =
                    type === "card" ? "Card" : type === "phone" ? "Phone" : "Name";
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setSearchType(type)}
                      className={
                        "rounded-full px-4 py-2 text-sm font-semibold ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] " +
                        (active
                          ? "bg-[#235F72] text-white ring-[#235F72]/20"
                          : "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100")
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results */}
            <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">
                      {searchTerm
                        ? `Search results for “${searchTerm}”`
                        : "All patients"}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-600">
                      {searchTerm
                        ? `Found ${localSearchResults.length} patient(s).`
                        : `Total: ${patientList.length}`}
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading patients…
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Card</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Age</th>
                        <th className="px-4 py-3">Gender</th>
                        <th className="px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {(searchTerm ? localSearchResults : patientList).map((patient) => (
                        <tr key={patient.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-200">
                                <UserRound className="h-4 w-4 text-[#235F72]" />
                              </span>
                              <div className="min-w-0">
                                <div className="truncate font-semibold text-gray-900">
                                  {patient.full_name || "—"}
                                </div>
                                <div className="mt-0.5 text-xs text-gray-600">ID: {patient.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{patient.card_number || "—"}</td>
                          <td className="px-4 py-3 text-gray-700">{patient.phone || "—"}</td>
                           <td className="px-4 py-3 text-gray-700">
                             {patient.age != null && patient.age !== ""
                               ? patient.age
                               : patient.date_of_birth
                               ? calculateAge(patient.date_of_birth)
                               : "—"}
                           </td>
                          <td className="px-4 py-3 text-gray-700 capitalize">{patient.gender || "—"}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleSelectPatient(patient)}
                              className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {(searchTerm ? localSearchResults : patientList).length === 0 ? (
                    <div className="px-4 py-12 text-center text-sm text-gray-600">
                      {searchTerm
                        ? "No patients match your search."
                        : "No patients available."}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Selected patient */}
            {selectedPatient ? (
              <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">Selected patient</div>
                    <div className="mt-1 truncate text-lg font-semibold text-[#235F72]">
                      {selectedPatient.full_name || "—"}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-600">
                      Card: {selectedPatient.card_number || "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPatient(null)}
                    className="inline-flex items-center justify-center rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Clear
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Contact</div>
                    <div className="mt-2 text-sm text-gray-900">Phone: {selectedPatient.phone || "—"}</div>
                    <div className="mt-1 text-sm text-gray-900">Email: {selectedPatient.email || "—"}</div>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Details</div>
                    <div className="mt-2 text-sm text-gray-900">
                      Age: {selectedPatient.age != null && selectedPatient.age !== ""
                        ? selectedPatient.age
                        : selectedPatient.date_of_birth
                        ? calculateAge(selectedPatient.date_of_birth)
                        : "—"}
                    </div>
                    <div className="mt-1 text-sm text-gray-900 capitalize">Gender: {selectedPatient.gender || "—"}</div>
                  </div>
                </div>

                {(selectedPatient.known_allergies ||
                  selectedPatient.chronic_conditions ||
                  selectedPatient.current_medications) && (
                  <div className="mt-4 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                    <div className="text-sm font-semibold text-gray-900">Medical</div>
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm text-gray-800">
                      {selectedPatient.known_allergies &&
                      selectedPatient.known_allergies !== "None" ? (
                        <div>
                          <div className="text-xs font-semibold text-gray-600">Allergies</div>
                          <div className="mt-1">{selectedPatient.known_allergies}</div>
                        </div>
                      ) : null}
                      {selectedPatient.chronic_conditions &&
                      selectedPatient.chronic_conditions !== "None" ? (
                        <div>
                          <div className="text-xs font-semibold text-gray-600">Conditions</div>
                          <div className="mt-1">{selectedPatient.chronic_conditions}</div>
                        </div>
                      ) : null}
                      {selectedPatient.current_medications &&
                      selectedPatient.current_medications !== "None" ? (
                        <div>
                          <div className="text-xs font-semibold text-gray-600">Medications</div>
                          <div className="mt-1">{selectedPatient.current_medications}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => handleViewHistory(selectedPatient)}
                    className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#085DB6] ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    View history
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAssignTests(selectedPatient)}
                    className="inline-flex items-center justify-center rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Assign tests
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditPatient(selectedPatient)}
                    className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Edit info
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientSearch;
